import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtUSD } from "../lib/api";
import { ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";

function buildTransferPayload({ fromId, mode, toId, toNumber, amount, desc }) {
  const payload = {
    from_account_id: fromId,
    amount: parseFloat(amount),
    description: desc || "Transfer",
  };
  if (mode === "internal") payload.to_account_id = toId;
  else payload.to_account_number = toNumber;
  return payload;
}

const Transfer = () => {
  const [accounts, setAccounts] = useState([]);
  const [fromId, setFromId] = useState("");
  const [mode, setMode] = useState("internal"); // internal | external
  const [toId, setToId] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAccounts = useCallback(async () => {
    const { data } = await api.get("/accounts");
    setAccounts(data);
    return data;
  }, []);

  useEffect(() => {
    loadAccounts().then((data) => {
      if (data.length) setFromId(data[0].id);
      if (data.length > 1) setToId(data[1].id);
    });
  }, [loadAccounts]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = buildTransferPayload({ fromId, mode, toId, toNumber, amount, desc });
      await api.post("/transactions/transfer", payload);
      toast.success(`Transferred ${fmtUSD(parseFloat(amount))}`);
      setAmount(""); setDesc("");
      await loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Transfer failed");
    } finally {
      setBusy(false);
    }
  };

  const src = useMemo(
    () => accounts.find((a) => a.id === fromId),
    [accounts, fromId]
  );

  const destinationAccounts = useMemo(
    () => accounts.filter((a) => a.id !== fromId),
    [accounts, fromId]
  );

  const exceedsBalance = src && parseFloat(amount) > src.balance;

  return (
    <div className="space-y-8 max-w-3xl">
      <Toaster richColors position="top-right" />
      <div>
        <div className="overline">Move money</div>
        <h1 className="font-display text-4xl mt-1">Transfer</h1>
      </div>

      <form onSubmit={submit} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] p-8 space-y-6">
        <div className="flex gap-2">
          {[["internal", "Between my accounts"], ["external", "To another Nashuka account"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)}
              className={`px-4 py-2 text-sm border ${mode === k ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white" : "border-[hsl(var(--border))]"}`}
              data-testid={`transfer-mode-${k}`}>{l}</button>
          ))}
        </div>

        <div>
          <label className="overline block mb-2">From</label>
          <select value={fromId} onChange={(e) => setFromId(e.target.value)}
            className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))]"
            data-testid="transfer-from-select">
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name} · {a.account_number.slice(-4)} · {fmtUSD(a.balance)}</option>
            ))}
          </select>
        </div>

        <div className="flex justify-center">
          <ArrowRight className="w-5 h-5 text-[hsl(var(--accent))]" />
        </div>

        {mode === "internal" ? (
          <div>
            <label className="overline block mb-2">To account</label>
            <select value={toId} onChange={(e) => setToId(e.target.value)} required
              className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))]"
              data-testid="transfer-to-select">
              {destinationAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name} · {a.account_number.slice(-4)}</option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className="overline block mb-2">Recipient account number (12 digits)</label>
            <input value={toNumber} onChange={(e) => setToNumber(e.target.value)} required
              className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))] font-mono"
              data-testid="transfer-to-number" placeholder="123456789012" />
          </div>
        )}

        <div>
          <label className="overline block mb-2">Amount (USD)</label>
          <input type="number" min="0.01" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))] font-mono text-2xl"
            data-testid="transfer-amount" />
          {exceedsBalance && (
            <div className="text-sm text-[hsl(var(--destructive))] mt-2">Exceeds available balance</div>
          )}
        </div>

        <div>
          <label className="overline block mb-2">Memo (optional)</label>
          <input value={desc} onChange={(e) => setDesc(e.target.value)}
            className="w-full bg-transparent border border-[hsl(var(--border))] px-4 py-3 outline-none focus:border-[hsl(var(--primary))]"
            data-testid="transfer-memo" />
        </div>

        <button type="submit" disabled={busy}
          className="w-full bg-[hsl(var(--primary))] text-white py-3 hover:bg-[hsl(var(--primary))]/90 disabled:opacity-60"
          data-testid="transfer-submit-btn">
          {busy ? "Sending…" : `Transfer ${amount ? fmtUSD(parseFloat(amount) || 0) : ""}`}
        </button>
      </form>
    </div>
  );
};

export default Transfer;
